import React from "react";
import { Button, Upload, Progress, Tag, Popconfirm, notification } from "antd";
import {
  VideoCameraAddOutlined,
  SaveOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { useVideoStore } from "./store/use-videos-store";
import {
  analyzeConnectionQuality,
  uploadVideoDirectly,
} from "../../../../libs/helpers";
import { useRouterStore } from "../../../../libs/stores";

const MAX_SIZE = 500 * 1024 * 1024; // 500 MB

const VideoCierreContenedor = () => {
  const container = useVideoStore((state) => state.container);
  const byPassSw = useRouterStore((state) => state.byPassSw);
  const [selectedVideo, setSelectedVideo] = React.useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [videoSizeMB, setVideoSizeMB] = React.useState<number | null>(null);
  const [estimatedTime, setEstimatedTime] = React.useState<string | null>(null);

  // ✅ NUEVO: Estado de conexión simplificado
  const [connectionInfo, setConnectionInfo] = React.useState(() =>
    analyzeConnectionQuality()
  );

  // ✅ NUEVO: Monitorear cambios de conexión
  React.useEffect(() => {
    const updateConnection = () => {
      const newInfo = analyzeConnectionQuality();
      setConnectionInfo(newInfo);
    };

    // Escuchar cambios de conectividad
    window.addEventListener("online", updateConnection);
    window.addEventListener("offline", updateConnection);

    // Escuchar cambios en la información de conexión
    const connection = (navigator as any).connection;
    if (connection) {
      connection.addEventListener("change", updateConnection);
    }

    // Actualizar cada 30 segundos para monitorear velocidad
    const interval = setInterval(updateConnection, 30000);

    return () => {
      window.removeEventListener("online", updateConnection);
      window.removeEventListener("offline", updateConnection);
      if (connection) {
        connection.removeEventListener("change", updateConnection);
      }
      clearInterval(interval);
    };
  }, []);

  // Limpiar preview URL cuando cambie
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // ✅ MEJORADO: Manejo de selección de archivo
  const handleFileChange = React.useCallback(
    async (info: any) => {
      const file = info.file;
      if (!file) return;

      // Validar tipo
      const allowedTypes = ["video/mp4"];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          title: "Formato no permitido",
          text: "Solo se permiten videos en formato MP4.",
          icon: "error",
          confirmButtonColor: "#1677ff",
        });
        return;
      }

      // Validar tamaño
      if (file.size > MAX_SIZE) {
        Swal.fire({
          title: "Video muy grande",
          text: `El video supera los ${MAX_SIZE / (1024 * 1024)}MB permitidos.`,
          icon: "error",
          confirmButtonColor: "#1677ff",
        });
        return;
      }

      // Limpiar preview anterior
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      // Crear nuevo preview
      const newPreviewUrl = URL.createObjectURL(file);
      setSelectedVideo(file);
      setPreviewUrl(newPreviewUrl);
      setVideoSizeMB(file.size / (1024 * 1024));

      // ✅ NUEVO: Calcular tiempo estimado basado en conexión actual
      const { speedMbps, canUpload } = connectionInfo;

      if (canUpload && speedMbps > 0) {
        // Calcular tiempo estimado más realista
        const fileSizeMB = file.size / (1024 * 1024);
        const uploadSpeedMBps = speedMbps / 8; // Convertir Mbps a MB/s
        const estimatedSeconds = fileSizeMB / uploadSpeedMBps;

        const minutes = Math.floor(estimatedSeconds / 60);
        const seconds = Math.round(estimatedSeconds % 60);

        if (minutes > 0) {
          setEstimatedTime(`${minutes}m ${seconds}s`);
        } else {
          setEstimatedTime(`${seconds}s`);
        }
      } else {
        setEstimatedTime("No se puede estimar");
      }

      // ✅ NUEVO: Mostrar advertencias según el tamaño y conexión
      const fileSizeMB = file.size / (1024 * 1024);

      if (fileSizeMB > 100 && !connectionInfo.canUpload) {
        notification.warning({
          message: "Video grande detectado",
          description: `Este video (${fileSizeMB.toFixed(
            1
          )}MB) requiere una mejor conexión a internet.`,
          duration: 6,
          placement: "topRight",
        });
      }
    },
    [previewUrl, connectionInfo]
  );

  // ✅ NUEVO: Función de upload simplificada
  const handleUpload = React.useCallback(async () => {
    if (!selectedVideo) {
      Swal.fire({
        title: "No hay video",
        text: "Selecciona un video antes de continuar.",
        icon: "warning",
        confirmButtonColor: "#1677ff",
      });
      return;
    }

    if (!container || container.length < 3) {
      Swal.fire({
        title: "Contenedor inválido",
        text: "Ingresa un número de contenedor válido (mínimo 3 caracteres).",
        icon: "warning",
        confirmButtonColor: "#1677ff",
      });
      return;
    }

    // ✅ CRÍTICO: Verificar conexión antes de subir
    const currentConnection = analyzeConnectionQuality();

    if (!byPassSw) {
      if (!currentConnection.canUpload) {
        let message = "";

        switch (currentConnection.quality) {
          case "sin-internet":
            message =
              "No hay conexión a internet. Conecta y vuelve a intentar.";
            break;
          case "internet-lento":
            message =
              "Tu conexión es muy lenta para subir videos. Conecta a WiFi o espera una mejor señal.";
            break;
          case "internet-regular":
            message =
              "Tu conexión es inestable. Para videos grandes se recomienda WiFi.";
            break;
          default:
            message = "Conexión insuficiente para subir el video.";
        }

        Swal.fire({
          title: "Conexión insuficiente",
          text: message,
          icon: "warning",
          confirmButtonColor: "#1677ff",
          confirmButtonText: "Entendido",
        });
        return;
      }
    }

    try {
      setLoading(true);
      setUploadProgress(0);

      console.log(
        `🚀 Iniciando upload directo (VideoCierreContenedor) - ${currentConnection.message}`
      );

      // ✅ UPLOAD DIRECTO SIN CACHE NI OFFLINE
      // Usando "videoCierreContenedor" como tipo y "full" como containerType
      const result = await uploadVideoDirectly(
        selectedVideo,
        container,
        "videoCierreContenedor", // ✅ Tipo específico para cierre de contenedor
        "full", // ✅ Container type para cierre (lleno)
        (progress) => setUploadProgress(progress)
      );

      // Completar progreso
      setUploadProgress(100);

      // ✅ MANEJAR RESPUESTA DEL SERVIDOR
      const response = result as any;

      if (response.existed) {
        // Video ya existe
        Swal.fire({
          title: "Video ya registrado",
          html: `
            <div>
              <p>${
                response.alert ||
                "Este contenedor ya tiene un video de cierre registrado."
              }</p>
              ${
                response.originalDate
                  ? `<p><small>📅 Fecha original: ${new Date(
                      response.originalDate
                    ).toLocaleDateString()}</small></p>`
                  : ""
              }
            </div>
          `,
          icon: "info",
          confirmButtonColor: "#1677ff",
          confirmButtonText: "Entendido",
        });
      } else {
        // Upload exitoso
        Swal.fire({
          title: "¡Video de cierre subido!",
          text:
            response.alert ||
            "El video de cierre del contenedor se subió correctamente al servidor.",
          icon: "success",
          confirmButtonColor: "#1677ff",
          timer: 3000,
          timerProgressBar: true,
        });
      }

      // ✅ LIMPIAR TODO DESPUÉS DEL ÉXITO
      clearSelection();
    } catch (error: any) {
      console.error("Error en upload de video cierre:", error);

      setUploadProgress(0);

      let errorTitle = "Error al subir video";
      let errorMessage = error.message || "Error desconocido";

      // ✅ MANEJAR ERRORES ESPECÍFICOS
      if (error.message?.includes("Sesión expirada")) {
        errorTitle = "Sesión expirada";
        errorMessage = "Tu sesión ha caducado. Inicia sesión nuevamente.";
      } else if (error.message?.includes("conexión")) {
        errorTitle = "Problema de conexión";
        errorMessage = "Verifica tu conexión a internet e intenta nuevamente.";
      } else if (error.message?.includes("Tiempo de espera")) {
        errorTitle = "Tiempo agotado";
        errorMessage =
          "El video es muy grande para tu conexión actual. Prueba con WiFi.";
      } else if (error.message?.includes("servidor")) {
        errorTitle = "Error del servidor";
        errorMessage =
          "Problema temporal del servidor. Intenta en unos minutos.";
      }

      Swal.fire({
        title: errorTitle,
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#1677ff",
        confirmButtonText: "Entendido",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedVideo, container]);

  // ✅ FUNCIÓN PARA LIMPIAR SELECCIÓN
  const clearSelection = React.useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedVideo(null);
    setPreviewUrl(null);
    setUploadProgress(0);
    setVideoSizeMB(null);
    setEstimatedTime(null);
  }, [previewUrl]);

  // ✅ NUEVO: Obtener estado visual de conexión
  const getConnectionDisplay = () => {
    const { quality, message, description, speedMbps, isWiFi, canUpload } =
      connectionInfo;

    switch (quality) {
      case "sin-internet":
        return {
          icon: <ExclamationCircleOutlined />,
          color: "red",
          text: message,
          description: description,
          showSpeed: false,
        };
      case "internet-lento":
        return {
          icon: <ExclamationCircleOutlined />,
          color: "volcano",
          text: message,
          description: `${description} (${speedMbps.toFixed(1)} Mbps)`,
          showSpeed: true,
        };
      case "internet-regular":
        return {
          icon: <ClockCircleOutlined />,
          color: "orange",
          text: message,
          description: `${description} (${speedMbps.toFixed(1)} Mbps)`,
          showSpeed: true,
        };
      case "internet-bueno":
        return {
          icon: <WifiOutlined />,
          color: "green",
          text: message,
          description: `${description} (${speedMbps.toFixed(1)} Mbps)`,
          showSpeed: true,
        };
      default:
        return {
          icon: <CheckCircleOutlined />,
          color: "blue",
          text: "Estado desconocido",
          description: "Verificando conexión...",
          showSpeed: false,
        };
    }
  };

  const connectionDisplay = getConnectionDisplay();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        gap: "5px",
        margin: "0px 20px",
        position: "relative",
      }}
    >
      {/* Selector de video o preview */}
      {!selectedVideo ? (
        <Upload
          accept="video/mp4"
          showUploadList={false}
          beforeUpload={(file) => {
            handleFileChange({ file });
            return false;
          }}
        >
          <Button
            type="dashed"
            danger
            size="large"
            style={{
              width: "100%",
              minWidth: "320px",
              maxWidth: "320px",
              margin: 0,
              fontSize: "0.9em",
              fontWeight: "bold",
              marginTop: "10px",
            }}
            icon={<VideoCameraAddOutlined />}
          >
            SUBIR CIERRE CONTENEDOR
          </Button>
        </Upload>
      ) : (
        <div
          style={{
            background: "#fff", border: "1px solid #dde3ec",
            borderRadius: "10px",
            padding: "20px",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <p>
            <strong>Video Cierre Seleccionado:</strong> {selectedVideo.name}
          </p>
          <p>
            <strong>Tamaño:</strong>{" "}
            {videoSizeMB ? `${videoSizeMB.toFixed(2)} MB` : "Calculando..."}
          </p>
          <p>
            <strong>Tiempo estimado:</strong> {estimatedTime || "Calculando..."}
            {connectionInfo.speedMbps > 0 && (
              <small style={{ color: "#666", marginLeft: "5px" }}>
                ({connectionInfo.speedMbps.toFixed(1)} Mbps)
              </small>
            )}
          </p>

          {/* ✅ NUEVO: Advertencia para archivos grandes */}
          {videoSizeMB && videoSizeMB > 100 && !connectionInfo.canUpload && (
            <div
              style={{
                background: "#fff2e8",
                border: "1px solid #ffbb96",
                borderRadius: "4px",
                padding: "8px",
                fontSize: "12px",
                color: "#d4380d",
                marginBottom: "10px",
              }}
            >
              ⚠️ Video grande ({videoSizeMB.toFixed(1)}MB). Se necesita WiFi o
              mejor conexión.
            </div>
          )}

          {/* Preview del video */}
          {previewUrl && (
            <video
              controls
              style={{
                width: "100%",
                maxHeight: "200px",
                marginBottom: "10px",
              }}
            >
              <source src={previewUrl} type={selectedVideo.type} />
            </video>
          )}

          {/* Barra de progreso */}
          {loading && (
            <div>
              <Progress
                percent={uploadProgress}
                status={uploadProgress < 100 ? "active" : "success"}
                style={{ marginBottom: "10px" }}
              />
              <div
                style={{ fontSize: "12px", color: "#666", textAlign: "center" }}
              >
                {uploadProgress < 100
                  ? `Subiendo video de cierre... ${uploadProgress}%`
                  : "Finalizando..."}
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Popconfirm
              title="¿Subir video de cierre al servidor?"
              description="El video se enviará directamente sin almacenamiento offline"
              onConfirm={handleUpload}
              zIndex={1601}
              okText="Sí, subir"
              cancelText="Cancelar"
              disabled={!connectionInfo.canUpload}
            >
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                loading={loading}
                disabled={
                  !container ||
                  loading ||
                  container.length < 3 ||
                  !connectionInfo.canUpload
                }
                style={{ fontSize: "1.1em" }}
              >
                SUBIR VIDEO
              </Button>
            </Popconfirm>
            <Button
              type="dashed"
              size="large"
              onClick={clearSelection}
              style={{ fontSize: "1.1em" }}
              icon={<ClearOutlined />}
              disabled={loading}
            >
              LIMPIAR
            </Button>
          </div>

          {/* ✅ NUEVO: Indicador de modo de subida */}
          <div style={{ marginTop: "10px", textAlign: "center" }}>
            {!connectionInfo.canUpload ? (
              <Tag color="red" icon={<ExclamationCircleOutlined />}>
                ❌ No se puede subir - {connectionInfo.message}
              </Tag>
            ) : (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                ✅ Listo para subir cierre -{" "}
                {connectionInfo.isWiFi ? "WiFi" : "Datos móviles"}
              </Tag>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCierreContenedor;
